import { sortBy } from 'lodash'
import upperFirst from 'lodash/upperFirst'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import {
  GraphCompareResponse,
  FeatureCollection,
  GraphData,
  FeatureCollection_TypeEnum,
  Feature,
  GraphStackResponse,
  GraphBucketResponse,
  GraphFilters,
  GraphBucket,
} from 'src/api/gen'
import { downloadCsv, getHeadersFromJsonList, JsonListToCsv } from 'src/commons/export'
import { formatDateExport, spreadBy } from 'src/commons/export/Export'
import BaseChartWrapper from 'src/commons/graphs/BaseChartWrapper'
import { ChartWrapper, DashboardGraphWrapper } from 'src/commons/graphs/GraphWrapper/GraphWrappers'
import useDebouncedCallback from 'src/commons/hooks/useDebouncedCallback'
import { getTranslation } from 'src/commons/i18n'
import { userHasPermission, Can } from 'src/commons/permissions'
import { IFilters, IGraphComponent } from 'src/commons/types'
import { createPayloadFromFilters } from 'src/commons/utils'
import WarnBanner from 'src/components/WarnBanner'
import { useProviders, useProviderUser } from 'src/layout/hooks'
import { getLayerFeatures, useMapLayer } from 'src/layout/MapLayerWrapper'

import { scrollToComponent } from '../hooks'

import {
  DeviceDeployedPerProvider,
  DevicesPerStatus,
  FleetAvailability,
  AverageFleetSizePerProvider,
  AverageFleetSizePerProviderOnRange,
  PercentDeviceDeployedPerZone,
  PercentDeviceDeployedPerZonePerProvider,
} from './charts'
import DeploymentMap from './DeploymentMap'
import {
  formatAverageFleetSizePerProvider,
  formatAverageFleetSizePerProviderOnRange,
  formatDeployedPerProvider,
  formatExportDeploymentMap,
  formatExportDevicesPerStatus,
  formatExportFleetAvailability,
  MAX_DISPLAYED_AREAS,
} from './exports'

function useDebouncedLoadDataCallback<DataType>(
  api_query: (params: GraphFilters) => Promise<DataType>,
  filters: IFilters,
  setLoading: (value: boolean) => void,
  setData: (value: DataType) => void
) {
  return useDebouncedCallback(async function () {
    try {
      setLoading(true)
      const payload = createPayloadFromFilters(filters)
      setData(await api_query(payload))
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  })
}

function exportAverageDeviceDeployedByZone(filters: IFilters, perProvider: boolean) {
  function formatDataWithDate(bucket: GraphCompareResponse) {
    const { t } = getTranslation()
    const orderedBuckets = sortBy(bucket.compareBy.buckets, 'key')
    return {
      [t('kpi.export.Date')]: formatDateExport(bucket.key, filters.granularity),
      ...spreadBy(orderedBuckets, `%`),
    }
  }
  function formatData(bucket: GraphCompareResponse) {
    const { t } = getTranslation()
    const orderedBuckets = sortBy(bucket.compareBy.buckets, 'key')
    return {
      [t('kpi.export.Area')]: bucket['key'],
      ...spreadBy(orderedBuckets),
    }
  }

  async function download(filename: string): Promise<any> {
    const exportFilters = { ...filters, limit: undefined }
    let data
    let jsonList
    if (perProvider) {
      data = await api.graphs.graphPercentDevicesDeployedPerZonePerProvider({
        data: createPayloadFromFilters(exportFilters),
      })
      jsonList = data.map(formatData)
    } else {
      data = await api.graphs.graphPercentDevicesDeployedPerZone({
        data: createPayloadFromFilters(exportFilters),
      })
      jsonList = data.map(formatDataWithDate)
    }

    const headers = getHeadersFromJsonList(jsonList)
    const csvContent = JsonListToCsv(jsonList, headers)
    downloadCsv(csvContent, filename)
  }
  return download
}
export function DeployedPerProviderComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const isProviderUser = useProviderUser()
  const { activeProviders } = useProviders()

  async function fetcher(): Promise<GraphCompareResponse[]> {
    const data = createPayloadFromFilters(props.filters)
    return api.graphs.graphDevicesDeployedPerProvider({ data, options: props.apiOptions })
  }

  function render(data: any, title?: string) {
    return <DeviceDeployedPerProvider title={title} graphReport={data} granularity={props.filters.granularity} />
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = formatDeployedPerProvider(props.filters.granularity)
  }

  let title
  let description
  if (isProviderUser && activeProviders.length < 2) {
    title = upperFirst(t('kpi.Deployed.title'))
    description = upperFirst(t('kpi.Deployed.description'))
  } else {
    title = upperFirst(t('kpi.DeployedPerProvider.title'))
    description = upperFirst(t('kpi.DeployedPerProvider.description'))
  }

  return (
    <DashboardGraphWrapper
      id={props.id}
      title={title}
      description={description}
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
      updateOn={[props.filters]}
    />
  )
}

export function AverageFleetSizePerProviderComponent(props: IGraphComponent) {
  const { t } = useTranslation()

  const [isHistogram, setIsHistogram] = useState<boolean>(false)
  const [loading, setLoading] = useState(true)

  const [compareData, setCompareData] = useState<GraphCompareResponse[] | undefined>(undefined)
  const [histogramData, setHistogramData] = useState<GraphBucket[] | undefined>(undefined)

  const fetchCompareData = useDebouncedLoadDataCallback(
    (payload: GraphFilters) =>
      api.graphs.graphAverageFleetSizePerProvider({ data: payload, options: props.apiOptions }),
    props.filters,
    setLoading,
    setCompareData
  )

  const fetchHistogramData = useDebouncedLoadDataCallback(
    (payload: GraphFilters) =>
      api.graphs.graphAverageFleetSizePerProviderOnRange({ data: payload, options: props.apiOptions }),
    props.filters,
    setLoading,
    setHistogramData
  )

  useEffect(() => {
    fetchHistogramData()
    fetchCompareData()
  }, [props.filters])

  useEffect(() => {
    // When toggling comparing, only fetch data if needed
    if (isHistogram && !histogramData) {
      fetchHistogramData()
    } else if (!isHistogram && !compareData) {
      fetchCompareData()
    }
  }, [isHistogram])

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    if (isHistogram) {
      formatExportBuckets = formatAverageFleetSizePerProviderOnRange()
    } else {
      formatExportBuckets = formatAverageFleetSizePerProvider(props.filters.granularity)
    }
  }

  const title = isHistogram
    ? upperFirst(t('kpi.AverageFleetSizePerProviderOnRange.title'))
    : upperFirst(t('kpi.AverageFleetSizePerProvider.title'))
  const description = isHistogram
    ? upperFirst(t('kpi.AverageFleetSizePerProviderOnRange.description'))
    : upperFirst(t('kpi.AverageFleetSizePerProvider.description'))

  return (
    <ChartWrapper
      id={props.id}
      title={title}
      description={description}
      formatExportBuckets={formatExportBuckets}
      toggles={[
        {
          active: isHistogram,
          activeIcon: 'line',
          activeText: t('Aggregate view'),
          inactiveIcon: 'bar',
          inactiveText: t('Comparison view'),
          onToggle: () => setIsHistogram(!isHistogram),
        },
      ]}
      loading={loading}
      data={isHistogram ? histogramData : compareData}
    >
      {isHistogram
        ? histogramData && (
            <AverageFleetSizePerProviderOnRange
              title={title}
              graphReport={histogramData}
              granularity={props.filters.granularity}
            />
          )
        : compareData && (
            <AverageFleetSizePerProvider
              title={title}
              graphReport={compareData}
              granularity={props.filters.granularity}
            />
          )}
    </ChartWrapper>
  )
}

function DevicesPerStatusComponent(props: IGraphComponent) {
  const { t } = useTranslation()

  async function fetcher(): Promise<GraphStackResponse[]> {
    const data = createPayloadFromFilters(props.filters)
    return api.graphs.graphDevicesPerStatus({ data, options: props.apiOptions })
  }

  function render(data: any, title?: string) {
    return <DevicesPerStatus title={title} graphReport={data} granularity={props.filters.granularity} />
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = formatExportDevicesPerStatus(props.filters.granularity)
  }

  return (
    <DashboardGraphWrapper
      title={upperFirst(t('kpi.StatusesPerDay.title'))}
      description={upperFirst(t('kpi.StatusesPerDay.description'))}
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
      halfContainer={true}
      updateOn={[props.filters]}
    />
  )
}

function FleetAvailabilityComponent(props: IGraphComponent) {
  const { t } = useTranslation()

  async function fetcher(): Promise<GraphBucketResponse[]> {
    const data = createPayloadFromFilters(props.filters)
    return api.graphs.graphFleetAvailability({ data, options: props.apiOptions })
  }

  function render(data: any, title?: string) {
    return <FleetAvailability title={title} graphReport={data} granularity={props.filters.granularity} />
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = formatExportFleetAvailability(props.filters.granularity)
  }

  return (
    <DashboardGraphWrapper
      title={upperFirst(t('kpi.FleetAvailability.title'))}
      description={upperFirst(t('kpi.FleetAvailability.description'))}
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
      halfContainer={true}
      updateOn={[props.filters]}
    />
  )
}

export function graphDataToFeatureCollection(graphData: GraphData, features: Feature[]): FeatureCollection {
  features.forEach((feature) => {
    const featureIndex = graphData.labels.findIndex((label) => label === feature.properties.label)
    feature.properties.value = graphData.datasets[0].data[featureIndex]
  })

  return {
    type: FeatureCollection_TypeEnum.FeatureCollection,
    features,
  }
}

function DeployedMapComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const mapLayers = useMapLayer()

  async function fetcher(): Promise<FeatureCollection> {
    const data = createPayloadFromFilters(props.filters)
    const layer = mapLayers.find((maplayer) => maplayer.id === data.layer)

    if (layer) {
      const layerFeatures = getLayerFeatures(layer)
      data.limit = layerFeatures.length || 20
      return api.graphs
        .graphAverageDeploymentDurationPerZone({ data, options: props.apiOptions })
        .then((results: GraphData) => {
          return graphDataToFeatureCollection(results, layerFeatures)
        })
    } else {
      return Promise.resolve({ type: FeatureCollection_TypeEnum.FeatureCollection, features: [] })
    }
  }

  function render(data: FeatureCollection, title?: string) {
    return <DeploymentMap title={title} data={data} />
  }

  let formatExportFeatures
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportFeatures = formatExportDeploymentMap()
  }

  return (
    <DashboardGraphWrapper
      title={upperFirst(t('kpi.DeployedMap.title'))}
      description={upperFirst(t('kpi.DeployedMap.description'))}
      fetcher={fetcher}
      render={render}
      formatExportFeatures={formatExportFeatures}
      updateOn={[props.filters]}
    />
  )
}

export function PercentageDeploymentByZoneComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const filters = props.filters
  filters.limit = MAX_DISPLAYED_AREAS
  const [perProvider, setPerProvider] = useState<boolean>(false)

  async function fetcher(): Promise<GraphCompareResponse[]> {
    const data = createPayloadFromFilters(filters)
    if (perProvider) {
      return api.graphs.graphPercentDevicesDeployedPerZonePerProvider({ data, options: props.apiOptions })
    } else {
      return api.graphs.graphPercentDevicesDeployedPerZone({ data, options: props.apiOptions })
    }
  }

  function render(data: any, title?: string) {
    if (perProvider) {
      return (
        <PercentDeviceDeployedPerZonePerProvider
          title={title}
          graphReport={data}
          granularity={props.filters.granularity}
        />
      )
    } else {
      return <PercentDeviceDeployedPerZone title={title} graphReport={data} granularity={props.filters.granularity} />
    }
  }
  function hideBanner(data: GraphCompareResponse[], bannerMessage?: string): string {
    if (perProvider) {
      return data.length == MAX_DISPLAYED_AREAS && bannerMessage ? bannerMessage : ''
    } else {
      const deviceDeployedLimit = Object.entries(data).filter((element) => {
        return element[1].compareBy.buckets.length == MAX_DISPLAYED_AREAS
      })
      return deviceDeployedLimit.length && bannerMessage ? bannerMessage : ''
    }
  }
  const toggles = userHasPermission(['read', 'trip'])
    ? [
        {
          active: perProvider,
          onToggle: () => setPerProvider(!perProvider),
          activeIcon: 'bar',
          inactiveIcon: 'line',
          activeText: t('kpi.PercentDeployed.toggleIconActiveText'),
          inactiveText: t('kpi.PercentDeployed.toggleIconInactiveText'),
        },
      ]
    : []
  let customExport
  if (userHasPermission(['analyze', 'kpi'])) {
    customExport = exportAverageDeviceDeployedByZone(filters, perProvider)
  }
  const title = perProvider
    ? upperFirst(t('kpi.PercentDeployed.PerProvider.title'))
    : upperFirst(t('kpi.PercentDeployed.title'))
  const description = perProvider
    ? upperFirst(t('kpi.PercentDeployed.PerProvider.description'))
    : upperFirst(t('kpi.PercentDeployed.description'))

  return (
    <DashboardGraphWrapper
      id={props.id}
      title={title}
      hideBanner={hideBanner}
      description={description}
      fetcher={fetcher}
      render={render}
      customExport={customExport}
      toggles={toggles}
      updateOn={[props.filters, perProvider]}
      bannerMessage={t('kpi.AverageDevicePerZone.warningAreaLimit', {
        limit: MAX_DISPLAYED_AREAS,
      })}
    />
  )
}

interface IDeploymentChartsProps {
  apiOptions?: Record<string, unknown>
  filters: IFilters
}

function DeploymentCharts(props: IDeploymentChartsProps) {
  useEffect(scrollToComponent)

  return (
    <BaseChartWrapper>
      <WarnBanner toDate={props.filters.range.to} />
      <Can I="read" an="device">
        <>
          <DeployedPerProviderComponent
            id="deployed-per-provider"
            filters={props.filters}
            apiOptions={props.apiOptions}
          />
          <AverageFleetSizePerProviderComponent filters={props.filters} apiOptions={props.apiOptions} />
        </>
      </Can>
      <Can I="summarize" an="kpi">
        <>
          <DevicesPerStatusComponent filters={props.filters} apiOptions={props.apiOptions} />
          <FleetAvailabilityComponent filters={props.filters} apiOptions={props.apiOptions} />
          <PercentageDeploymentByZoneComponent filters={props.filters} apiOptions={props.apiOptions} />
          <DeployedMapComponent filters={props.filters} apiOptions={props.apiOptions} />
        </>
      </Can>
    </BaseChartWrapper>
  )
}

export default DeploymentCharts
